/**
 * Cloudflare Turnstile, the spam check on every form. It loads the first time someone uses a form and
 * stays invisible unless Cloudflare wants a click ("Verify you are human"). Forms without a site key
 * (data-turnstile="") skip it
 */
const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

// How long sending waits for the check before telling the visitor it didn't finish
const TIME_LIMIT_MS = 10000

const widgets = new WeakMap()
let loading = null

const loadTurnstile = () => {
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = SCRIPT
      script.async = true
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile didn't start")))
      script.onerror = () => {
        script.remove()
        reject(new Error("Turnstile didn't load"))
      }
      document.head.append(script)
    }).catch((error) => {
      // Let the next attempt try again
      loading = null
      throw error
    })
  }

  return loading
}

const settle = (widget, token) => {
  widget.waiting.splice(0).forEach((resolve) => resolve(token))
}

/**
 * Start the check for a form, or start it again if it failed last time
 */
export const startTurnstile = (form) => {
  const sitekey = form.dataset.turnstile
  const container = form.querySelector(".cf-turnstile")
  const existing = widgets.get(form)

  if (!sitekey || !container || (existing && existing.status !== "failed")) {
    return
  }

  const widget = { token: "", waiting: existing?.waiting ?? [], id: null, status: "loading" }
  widgets.set(form, widget)

  const failed = () => {
    widget.status = "failed"
    settle(widget, "")
  }

  loadTurnstile()
    .then((turnstile) => {
      widget.id = turnstile.render(container, {
        sitekey,
        appearance: "interaction-only",
        // The wide widget needs 300px; narrow phones get the compact one
        size: form.clientWidth < 300 ? "compact" : "flexible",
        callback: (token) => {
          widget.token = token
          widget.status = "ready"
          settle(widget, token)
        },
        // Cloudflare wants a click: show the box, and stop anyone waiting so they can be told
        "before-interactive-callback": () => {
          widget.status = "interactive"
          container.classList.add("is-visible")
          settle(widget, "")
        },
        "after-interactive-callback": () => {
          container.classList.remove("is-visible")
        },
        "expired-callback": () => {
          widget.token = ""
          // The news grid rebuilds its newsletter card when sorting, leaving this widget behind
          if (!container.isConnected) {
            turnstile.remove(widget.id)
            widgets.delete(form)
          }
        },
        "error-callback": failed,
        "unsupported-callback": failed,
      })
    })
    .catch(failed)
}

/**
 * @returns {Promise<{token: string, status: string}>} the check's token, or "" with why it isn't
 *   ready: "interactive" (needs a click), "failed" (didn't load or errored) or "loading" (too slow).
 *   Forms without a site key get status "off"
 */
export const turnstileToken = async (form) => {
  if (!form.dataset.turnstile) {
    return { token: "", status: "off" }
  }

  startTurnstile(form)
  const widget = widgets.get(form)

  if (widget.token) {
    return { token: widget.token, status: widget.status }
  }

  if (widget.status === "interactive") {
    return { token: "", status: "interactive" }
  }

  const token = await new Promise((resolve) => {
    widget.waiting.push(resolve)
    setTimeout(() => resolve(""), TIME_LIMIT_MS)
  })

  return { token, status: widget.status }
}

/**
 * Each token works once, so get a fresh one after a send
 */
export const resetTurnstile = (form) => {
  const widget = widgets.get(form)

  if (widget && widget.id !== null && window.turnstile) {
    widget.token = ""
    window.turnstile.reset(widget.id)
  }
}
